import React, { useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

interface LazyRenderProps {
  children: React.ReactNode;
  options?: IntersectionObserverInit;
  placeholder?: React.ReactNode;
}

/*
 * Renders children when the component comes into view.
 * This component uses the intersection observer and accepts optional `IntersectionObserverInit` parameters
 * as the `options` prop.
 *
 * An optional `placeholder` is rendered until the component comes into view. Pass a placeholder that
 * occupies the same space as the eventual children (e.g. a skeleton) to reserve layout space and avoid
 * cumulative layout shift. When omitted, nothing is rendered before intersection.
 *
 * Example:
 * ```ts
 * <LazyRender
 *   options={{
 *     // render the child 200px before the component is in view
       rootMargin: '200px',
     }}
 *   placeholder={<ProductCarouselSkeleton />}
 * >
 *     <ExpensiveDataFetchingComponent/>
 * </LazyRender>
 * ```
 */
export const LazyRender = ({ children, options, placeholder = null }: LazyRenderProps) => {
  const [renderChildren, setRenderChildren] = useState(false);

  const impressionRef = useImpressionRef<HTMLDivElement>(() => {
    setRenderChildren(true);
  }, options);

  return <div ref={impressionRef}>{renderChildren ? children : placeholder}</div>;
};
